import { useEffect } from 'react'
import { Row, Table } from '@tanstack/react-table'
import { useAppDispatch } from 'helpers/store'
import { useTypedSelector } from 'helpers/reducers'
import { clearTableLastClickedRow, setTableLastClickedRow } from 'store/ui/UiReducer'

interface UseTanstackTableScrollRestoreProps<TData> {
    table: Table<TData>
    tableId?: string
}

const ROW_MEMORY_MAX_AGE_MS = 1000 * 60 * 60 * 24
const ROW_RESTORE_MIN_DELAY_MS = 500
const ROW_HIGHLIGHT_DURATION_MS = 2500
const ROW_HIGHLIGHT_COLOR = '#0c97a3'
const ROW_HIGHLIGHT_BACKGROUND = 'rgba(12, 151, 163, 0.18)'
const ROW_HIGHLIGHT_SHADOW = 'inset 0 0 0 2px #0c97a3'

export const useTanstackTableScrollRestore = <TData extends { id?: string | number }>(
    props: UseTanstackTableScrollRestoreProps<TData>
) => {
        const clearHighlightedRow = (rowNode: HTMLElement) => {
            rowNode.style.backgroundColor = ''
            rowNode.style.boxShadow = ''
            rowNode.removeAttribute('data-scroll-restore-highlight')
        }

        const highlightRow = (rowNode: HTMLElement) => {
            rowNode.style.backgroundColor = ROW_HIGHLIGHT_BACKGROUND
            rowNode.style.boxShadow = ROW_HIGHLIGHT_SHADOW
            rowNode.style.setProperty('--scroll-restore-highlight-color', ROW_HIGHLIGHT_COLOR)
            rowNode.setAttribute('data-scroll-restore-highlight', '1')
        }

    const dispatch = useAppDispatch()

    const tableScrollKey = props.tableId ?? ''

    const rememberedRow = useTypedSelector((state) =>
        tableScrollKey ? state.ui.tableRows?.[tableScrollKey] : undefined
    )

    const rowsIdentity = props.table.getRowModel().rows.map((row) => row.id).join('|')

    const rememberRowClick = (row: Row<TData>) => {
        if (!tableScrollKey) {
            return
        }

        const rowOriginalId = (row.original as TData)?.id

        dispatch(
            setTableLastClickedRow({
                tableId: tableScrollKey,
                rowId: row.id,
                rowOriginalId,
                rowIndex: row.index,
            })
        )
    }

    useEffect(() => {
        if (!tableScrollKey || !rememberedRow) {
            return
        }

        const memoryAgeMs = Date.now() - rememberedRow.timestamp

        if (memoryAgeMs > ROW_MEMORY_MAX_AGE_MS) {
            dispatch(clearTableLastClickedRow({ tableId: tableScrollKey }))
            return
        }

        if (!rowsIdentity) {
            return
        }

        const initialDelayMs = memoryAgeMs < ROW_RESTORE_MIN_DELAY_MS
            ? ROW_RESTORE_MIN_DELAY_MS - memoryAgeMs
            : 0

        let initialDelayTimer: number | null = null
        let retryTimer: number | null = null
        let highlightTimer: number | null = null
        let frame: number | null = null
        const maxAttempts = 8
        let attempts = 0

        const tryRestoreScroll = () => {
            attempts += 1

            const wrappers = document.querySelectorAll<HTMLElement>('[data-table-scroll-key]')
            const tableWrapper = Array.from(wrappers).find(
                (node) => node.dataset.tableScrollKey === tableScrollKey
            )

            if (!tableWrapper) {
                if (attempts < maxAttempts) {
                    retryTimer = window.setTimeout(tryRestoreScroll, 120)
                }
                return
            }

            const rows = tableWrapper.querySelectorAll<HTMLElement>('tr[data-row-scroll-id]')
            const targetRowById = Array.from(rows).find(
                (node) => node.dataset.rowScrollId === rememberedRow.rowId
            )

            const targetRowByOriginalId =
                targetRowById ||
                (rememberedRow.rowOriginalId !== undefined &&
                rememberedRow.rowOriginalId !== null
                    ? Array.from(rows).find(
                        (node) =>
                            node.dataset.rowOriginalId ===
                            String(rememberedRow.rowOriginalId)
                    )
                    : undefined)

            const targetRowByIndex =
                targetRowByOriginalId ||
                (typeof rememberedRow.rowIndex === 'number' &&
                rememberedRow.rowIndex >= 0 &&
                rememberedRow.rowIndex < rows.length
                    ? rows[rememberedRow.rowIndex]
                    : undefined)

            if (!targetRowByIndex) {
                if (attempts < maxAttempts) {
                    retryTimer = window.setTimeout(tryRestoreScroll, 120)
                }
                return
            }

            const scrollContainer = tableWrapper.querySelector<HTMLElement>('div.overflow-auto')
            if (scrollContainer) {
                const headerHeight = tableWrapper.querySelector<HTMLElement>('thead')?.offsetHeight ?? 0
                const targetTop = Math.max(0, targetRowByIndex.offsetTop - headerHeight)
                scrollContainer.scrollTo({
                    top: targetTop,
                    behavior: 'auto',
                })
            } else {
                targetRowByIndex.scrollIntoView({
                    block: 'start',
                    behavior: 'auto',
                })
            }

            const previouslyHighlightedRows = tableWrapper.querySelectorAll<HTMLElement>(
                'tr[data-scroll-restore-highlight="1"]'
            )

            previouslyHighlightedRows.forEach((rowNode) => {
                clearHighlightedRow(rowNode)
            })

            highlightRow(targetRowByIndex)

            highlightTimer = window.setTimeout(() => {
                clearHighlightedRow(targetRowByIndex)
            }, ROW_HIGHLIGHT_DURATION_MS)

            dispatch(clearTableLastClickedRow({ tableId: tableScrollKey }))
        }

        const startRestore = () => {
            frame = window.requestAnimationFrame(tryRestoreScroll)
        }

        if (initialDelayMs > 0) {
            initialDelayTimer = window.setTimeout(startRestore, initialDelayMs)
        } else {
            startRestore()
        }

        return () => {
            if (frame !== null) {
                window.cancelAnimationFrame(frame)
            }
            if (initialDelayTimer !== null) {
                window.clearTimeout(initialDelayTimer)
            }
            if (retryTimer !== null) {
                window.clearTimeout(retryTimer)
            }
            if (highlightTimer !== null) {
                window.clearTimeout(highlightTimer)
            }
        }
    }, [dispatch, rememberedRow, rowsIdentity, tableScrollKey])

    return {
        tableScrollKey,
        rememberRowClick,
    }
}
